import { BigNumber } from '@ethersproject/bignumber'
import { TransactionResponse } from '@ethersproject/providers'
import { Currency, currencyEquals, ETHER, Token, TokenAmount, WETH9 } from '@uniswap/sdk-core'
import React, { useCallback, useContext, useState } from 'react'
import { Plus } from 'react-feather'
import ReactGA from 'react-ga'
import { RouteComponentProps } from 'react-router-dom'
import { Text } from 'rebass'
import { ThemeContext } from 'styled-components'
import { ButtonError, ButtonLight, ButtonPrimary } from '../../components/Button'
import { BlueCard, LightCard } from '../../components/Card'
import { AutoColumn, ColumnCenter } from '../../components/Column'
import TransactionConfirmationModal, { ConfirmationModalContent } from '../../components/TransactionConfirmationModal'
import CurrencyInputPanel from '../../components/CurrencyInputPanel'
import DoubleCurrencyLogo from '../../components/DoubleLogo'
import { MinimalPositionCard } from '../../components/PositionCard'
import Row, { RowBetween, RowFlat } from '../../components/Row'
import { v4 as uuidv4 } from 'uuid'
import { V2_ROUTER_ADDRESS, ZERO_ADDRESS, ZERO_PERCENT } from '../../constants'
import { useContract, useV2RouterContract, useV2RouterForCampaignContract } from '../../hooks/useContract'
import { PairState, useV2FactoryPairExists } from '../../hooks/useV2Pairs'
import { useActiveWeb3React } from '../../hooks'
import { useCurrency } from '../../hooks/Tokens'
import { ApprovalState, useApproveCallback } from '../../hooks/useApproveCallback'
import { useIsSwapUnsupported } from '../../hooks/useIsSwapUnsupported'
import useTransactionDeadline from '../../hooks/useTransactionDeadline'
import { useWalletModalToggle } from '../../state/application/hooks'
import { Field } from '../../state/mint/actions'
import { useDerivedMintInfo, useMintActionHandlers, useMintState } from '../../state/mint/hooks'

import { useTransactionAdder } from '../../state/transactions/hooks'
import { useIsExpertMode, usePairAdder, useUserSlippageTolerance } from '../../state/user/hooks'
import { TYPE } from '../../theme'
import { calculateGasMargin, calculateSlippageAmount } from '../../utils'
import { maxAmountSpend } from '../../utils/maxAmountSpend'
import { wrappedCurrency } from '../../utils/wrappedCurrency'
import { Dots, Wrapper } from '../Pool/styleds'
import { ConfirmAddModalBottom } from './ConfirmAddModalBottom'
import { currencyId } from '../../utils/currencyId'
import { PoolPriceBar } from './PoolPriceBar'
import UnsupportedCurrencyFooter from 'components/swap/UnsupportedCurrencyFooter'
import { Helmet } from 'react-helmet'
import useFarmingPools from 'hooks/useFarmingPools'
import { StyledTabButton, Tabs } from 'pages/FarmPool'
import { CURRENT_CHAIN, V2_FACTORY_ADDRESS, V2_ROUTER_FOR_CAMPAIGN } from '@hyperswap/addresses'
import { generateSignature } from 'utils/cryptoUtils'
import axios from 'axios'
import { API_GASLESS_BASE_URL } from 'constants/links'
import {
  CurrencyDropdown,
  CustomAppBody,
  MobileStepperWrapper,
  RowContainer,
  ScrollablePage,
  Step,
  StepContent,
  StepDescription,
  StepLine,
  StepNumber,
  StepperContainer,
  StepperHeader,
  StepperWrapper,
  StepReview,
  StepTitle,
} from 'pages/AddLiquidity/styled'
import { abi as IUniswapV2FactoryABI } from '@uniswap/v2-core/build/IUniswapV2Factory.json'
import { AddLiquidityAddRemoveTabs } from 'components/NavigationTabs/AddLiquidityNavigationTabs'
import TagButton from 'components/TagButton'

export default function AddLiquidity({
  match: {
    params: { currencyIdA, currencyIdB },
  },
  history,
}: RouteComponentProps<{ currencyIdA?: string; currencyIdB?: string }>) {
  const { account, chainId, library } = useActiveWeb3React()
  const theme = useContext(ThemeContext)
  const { pools } = useFarmingPools()
  const currencyA = useCurrency(currencyIdA)
  const currencyB = useCurrency(currencyIdB)
  const pairExists = useV2FactoryPairExists(
    currencyA instanceof Token ? currencyA.address : WETH9[CURRENT_CHAIN].address,
    currencyB instanceof Token ? currencyB.address : WETH9[CURRENT_CHAIN].address
  )
  const oneCurrencyIsWETH = Boolean(
    chainId &&
      ((currencyA && currencyEquals(currencyA, WETH9[CURRENT_CHAIN])) ||
        (currencyB && currencyEquals(currencyB, WETH9[CURRENT_CHAIN])))
  )

  const toggleWalletModal = useWalletModalToggle() // toggle wallet when disconnected
  const factoryContract = useContract(V2_FACTORY_ADDRESS, IUniswapV2FactoryABI)

  const expertMode = useIsExpertMode()

  // mint state
  const { independentField, typedValue, otherTypedValue } = useMintState()
  const {
    dependentField,
    currencies,
    pair,
    pairState,
    currencyBalances,
    parsedAmounts,
    price,
    noLiquidity,
    liquidityMinted,
    poolTokenPercentage,
    error,
  } = useDerivedMintInfo(currencyA ?? undefined, currencyB ?? undefined)
  const pairAddress = pair?.liquidityToken?.address
  const farmingPoolIndex = pools.findIndex((pool: any) => pool.lpToken === pairAddress)
  const farmingPoolExists = farmingPoolIndex !== -1
  const [addInFarmingPool, setAddInFarmingPool] = useState(true)
  const addPair = usePairAdder()
  const { onFieldAInput, onFieldBInput } = useMintActionHandlers(noLiquidity)

  const isValid = !error

  // modal and loading
  const [showConfirm, setShowConfirm] = useState<boolean>(false)
  const [attemptingTxn, setAttemptingTxn] = useState<boolean>(false) // clicked confirm

  // txn values
  const deadline = useTransactionDeadline() // custom from users settings
  const [allowedSlippage] = useUserSlippageTolerance() // custom from users
  const [txHash, setTxHash] = useState<string>('')

  // get formatted amounts
  const formattedAmounts = {
    [independentField]: typedValue,
    [dependentField]: noLiquidity ? otherTypedValue : parsedAmounts[dependentField]?.toSignificant(6) ?? '',
  }

  // get the max amounts user can add
  const maxAmounts: { [field in Field]?: TokenAmount } = [Field.CURRENCY_A, Field.CURRENCY_B].reduce(
    (accumulator, field) => {
      return {
        ...accumulator,
        [field]: maxAmountSpend(currencyBalances[field]),
      }
    },
    {}
  )

  const atMaxAmounts: { [field in Field]?: TokenAmount } = [Field.CURRENCY_A, Field.CURRENCY_B].reduce(
    (accumulator, field) => {
      return {
        ...accumulator,
        [field]: maxAmounts[field]?.equalTo(parsedAmounts[field] ?? '0'),
      }
    },
    {}
  )

  // check whether the user has approved the router on the tokens
  const [approvalA, approveACallback] = useApproveCallback(parsedAmounts[Field.CURRENCY_A], V2_ROUTER_ADDRESS)
  const [approvalB, approveBCallback] = useApproveCallback(parsedAmounts[Field.CURRENCY_B], V2_ROUTER_ADDRESS)

  const [approvalAForCampaign, approveAForCampaignCallback] = useApproveCallback(
    parsedAmounts[Field.CURRENCY_A],
    V2_ROUTER_FOR_CAMPAIGN ? V2_ROUTER_FOR_CAMPAIGN : ZERO_ADDRESS
  )
  const [approvalBForCampaign, approveBForCampaignCallback] = useApproveCallback(
    parsedAmounts[Field.CURRENCY_B],
    V2_ROUTER_FOR_CAMPAIGN ? V2_ROUTER_FOR_CAMPAIGN : ZERO_ADDRESS
  )

  const addTransaction = useTransactionAdder()

  const router = useV2RouterContract()
  const routerForCampaigns = useV2RouterForCampaignContract()

  const swapIsInvalidWithApproval =
    (farmingPoolExists &&
      addInFarmingPool &&
      (approvalAForCampaign !== ApprovalState.APPROVED || approvalBForCampaign !== ApprovalState.APPROVED)) ||
    ((!farmingPoolExists || (farmingPoolExists && !addInFarmingPool)) &&
      (approvalA !== ApprovalState.APPROVED || approvalB !== ApprovalState.APPROVED))

  async function onAdd() {
    if (!chainId || !library || !account || !router) return

    const { [Field.CURRENCY_A]: parsedAmountA, [Field.CURRENCY_B]: parsedAmountB } = parsedAmounts
    if (!parsedAmountA || !parsedAmountB || !currencyA || !currencyB || !deadline) {
      return
    }

    const amountsMin = {
      [Field.CURRENCY_A]: calculateSlippageAmount(parsedAmountA, noLiquidity ? ZERO_PERCENT : allowedSlippage)[0],
      [Field.CURRENCY_B]: calculateSlippageAmount(parsedAmountB, noLiquidity ? ZERO_PERCENT : allowedSlippage)[0],
    }

    const usedRouter =
      farmingPoolExists === true && addInFarmingPool === true && routerForCampaigns ? routerForCampaigns : router

    let estimate,
      method: (...args: any) => Promise<TransactionResponse>,
      args: Array<string | string[] | number>,
      value: BigNumber | null
    if (currencyA === ETHER || currencyB === ETHER) {
      const tokenBIsETH = currencyB === ETHER
      estimate = usedRouter.estimateGas.addLiquidityETH
      method = usedRouter.addLiquidityETH
      const tokenMin = amountsMin[tokenBIsETH ? Field.CURRENCY_A : Field.CURRENCY_B].toString()
      const tokenMinDecreased = BigNumber.from(tokenMin).mul(90).div(100).toString()

      args = [
        wrappedCurrency(tokenBIsETH ? currencyA : currencyB, chainId)?.address ?? '', // token
        (tokenBIsETH ? parsedAmountA : parsedAmountB).raw.toString(), // token desired
        tokenMinDecreased, // token min
        amountsMin[tokenBIsETH ? Field.CURRENCY_B : Field.CURRENCY_A].toString(), // eth min
        account,
        deadline.toHexString(),
        ...(addInFarmingPool && farmingPoolExists ? [farmingPoolIndex] : []),
      ]
      value = BigNumber.from((tokenBIsETH ? parsedAmountB : parsedAmountA).raw.toString())
    } else {
      estimate = usedRouter.estimateGas.addLiquidity
      method = usedRouter.addLiquidity
      args = [
        wrappedCurrency(currencyA, chainId)?.address ?? '',
        wrappedCurrency(currencyB, chainId)?.address ?? '',
        parsedAmountA.raw.toString(),
        parsedAmountB.raw.toString(),
        amountsMin[Field.CURRENCY_A].toString(),
        amountsMin[Field.CURRENCY_B].toString(),
        account,
        deadline.toHexString(),
        ...(addInFarmingPool && farmingPoolExists ? [farmingPoolIndex] : []),
      ]
      value = null
    }

    setAttemptingTxn(true)
    await estimate(...args, value ? { value } : {})
      .then((estimatedGasLimit) => {
        console.log('AddLiquidityV2 estimatedGasLimit', estimatedGasLimit.toString())
        return method(...args, {
          ...(value ? { value } : {}),
          gasLimit: calculateGasMargin(estimatedGasLimit),
        }).then((response) => {
          setAttemptingTxn(false)

          addTransaction(response, {
            summary:
              'Add ' +
              parsedAmounts[Field.CURRENCY_A]?.toSignificant(3) +
              ' ' +
              currencies[Field.CURRENCY_A]?.symbol +
              ' and ' +
              parsedAmounts[Field.CURRENCY_B]?.toSignificant(3) +
              ' ' +
              currencies[Field.CURRENCY_B]?.symbol,
          })

          setTxHash(response.hash)

          if (pairState === PairState.EXISTS && pair) {
            addPair(pair)
          }

          ReactGA.event({
            category: 'Liquidity',
            action: 'Add',
            label: [currencies[Field.CURRENCY_A]?.symbol, currencies[Field.CURRENCY_B]?.symbol].join('/'),
          })
        })
      })
      .catch((error) => {
        setAttemptingTxn(false)
        // we only care if the error is something _other_ than the user rejected the tx
        if (error?.code !== 4001) {
          console.error(error)
        }
      })
  }

  const [loadingCreate, setLoadingCreate] = useState(false)
  const [errorCreate, setErrorCreate] = useState('')
  const [successCreate, setSuccessCreate] = useState(false)

  async function onCreate() {
    if (!chainId || !library || !account || !router || !factoryContract) return
    setLoadingCreate(true)
    const currencyAAddress = currencyA instanceof Token ? currencyA.address : WETH9[CURRENT_CHAIN].address
    const currencyBAddress = currencyB instanceof Token ? currencyB.address : WETH9[CURRENT_CHAIN].address

    const currencyASymbol = currencyA?.symbol
    const currencyBSymbol = currencyB?.symbol

    const timestamp = Math.floor(Date.now() / 1000)
    const message = `Please sign this message to create pair ${currencyASymbol}-${currencyBSymbol}. Timestamp: ${timestamp}`

    try {
      const signer = library.getSigner()
      const signature = await signer.signMessage(message)
      setErrorCreate('')
      setSuccessCreate(false)
      const uuid = uuidv4()
      const body = JSON.stringify({
        taskId: uuid,
        address: account,
        signature: signature,
        message: message,
        tokenA: currencyAAddress,
        tokenB: currencyBAddress,
        version: 'v2',
      })

      const config = {
        method: 'post',
        url: `${API_GASLESS_BASE_URL}/api/relay-create-pair`,
        headers: {
          'x-signature': generateSignature(message),
          'Content-Type': 'application/json',
        },
        data: body,
      }

      const checkStatus = async () => {
        factoryContract.on('PairCreated', (token0, token1, pairAddress) => {
          if (
            (token0.toLowerCase() === currencyAAddress.toLowerCase() &&
              token1.toLowerCase() === currencyBAddress.toLowerCase()) ||
            (token0.toLowerCase() === currencyBAddress.toLowerCase() &&
              token1.toLowerCase() === currencyAAddress.toLowerCase())
          ) {
            console.log(`LP address: ${pairAddress}`)
            factoryContract.removeAllListeners('PairCreated')
            setLoadingCreate(false)
            setSuccessCreate(true)
            setErrorCreate('')
            handleStep(2, true)
          }
        })
      }

      axios
        .request(config)
        .then(async (response) => {
          if (response.data.success === true) {
            await checkStatus()
          } else {
            console.log(response)
            setLoadingCreate(false)
            setSuccessCreate(false)
            setErrorCreate('An error has occurred. Please try again.')
          }
        })
        .catch((error: any) => {
          console.error(error)
          setLoadingCreate(false)
          setSuccessCreate(false)
          setErrorCreate('An error has occurred. Please try again.')
        })
    } catch (error) {
      setLoadingCreate(false)
      setSuccessCreate(false)
      console.error('Error sending transaction:', error)
    }
  }

  const clearAll = useCallback(() => {
    onFieldAInput('')
    onFieldBInput('')

    history.push(`/add/v2/HYPE`)
    history.go(0)
  }, [history, onFieldAInput, onFieldBInput])

  const modalHeader = () => {
    return noLiquidity ? (
      <AutoColumn gap="20px" style={{ marginBottom: '12px' }}>
        <LightCard mt="20px" borderRadius="20px">
          <RowFlat style={{ alignItems: 'center', gap: '12px' }}>
            <Text fontSize="24px" fontWeight={500} lineHeight="42px" marginRight={10}>
              {currencies[Field.CURRENCY_A]?.symbol + '/' + currencies[Field.CURRENCY_B]?.symbol}
            </Text>
            <DoubleCurrencyLogo
              currency0={currencies[Field.CURRENCY_A]}
              currency1={currencies[Field.CURRENCY_B]}
              size={30}
            />
          </RowFlat>
        </LightCard>
      </AutoColumn>
    ) : (
      <AutoColumn gap="20px" style={{ marginBottom: '12px' }}>
        <RowFlat style={{ alignItems: 'center', gap: '12px' }}>
          <Text fontSize="24px" fontWeight={500} lineHeight="42px" marginRight={10}>
            {liquidityMinted?.toSignificant(6)}
          </Text>
          <DoubleCurrencyLogo
            currency0={currencies[Field.CURRENCY_A]}
            currency1={currencies[Field.CURRENCY_B]}
            size={30}
          />
        </RowFlat>
        <Row>
          <Text fontSize="24px">
            {currencies[Field.CURRENCY_A]?.symbol + '/' + currencies[Field.CURRENCY_B]?.symbol + ' Pool Tokens'}
          </Text>
        </Row>
        <TYPE.italic fontSize={12} textAlign="left" padding={'8px 0 0 0 '}>
          {`Output is estimated. If the price changes by more than ${allowedSlippage.toSignificant(
            4
          )}% your transaction will revert.`}
        </TYPE.italic>
      </AutoColumn>
    )
  }

  const modalBottom = () => {
    return (
      <ConfirmAddModalBottom
        price={price}
        currencies={currencies}
        parsedAmounts={parsedAmounts}
        noLiquidity={noLiquidity}
        onAdd={onAdd}
        poolTokenPercentage={poolTokenPercentage}
      />
    )
  }

  const pendingText = `Supplying ${parsedAmounts[Field.CURRENCY_A]?.toSignificant(6)} ${
    currencies[Field.CURRENCY_A]?.symbol
  } and ${parsedAmounts[Field.CURRENCY_B]?.toSignificant(6)} ${currencies[Field.CURRENCY_B]?.symbol}`

  const handleCurrencyASelect = useCallback(
    (currencyA: Currency) => {
      const newCurrencyIdA = currencyId(currencyA)
      if (newCurrencyIdA === currencyIdB) {
        history.push(`/add/v2/${currencyIdB}/${currencyIdA}`)
      } else {
        history.push(`/add/v2/${newCurrencyIdA}/${currencyIdB}`)
      }
    },
    [currencyIdB, history, currencyIdA]
  )
  const handleCurrencyBSelect = useCallback(
    (currencyB: Currency) => {
      const newCurrencyIdB = currencyId(currencyB)
      if (currencyIdA === newCurrencyIdB) {
        if (currencyIdB) {
          history.push(`/add/v2/${currencyIdB}/${newCurrencyIdB}`)
        } else {
          history.push(`/add/v2/${newCurrencyIdB}`)
        }
      } else {
        history.push(`/add/v2/${currencyIdA ? currencyIdA : 'HYPE'}/${newCurrencyIdB}`)
      }
    },
    [currencyIdA, history, currencyIdB]
  )

  const handleDismissConfirmation = useCallback(() => {
    setShowConfirm(false)
    // if there was a tx hash, we want to clear the input
    if (txHash) {
      onFieldAInput('')
    }
    setTxHash('')
  }, [onFieldAInput, txHash])

  const addIsUnsupported = useIsSwapUnsupported(currencies?.CURRENCY_A, currencies?.CURRENCY_B)

  const steps = [
    { title: 'Step 1', description: 'Select token pair' },
    { title: 'Step 2', description: 'Create pool' },
    { title: 'Step 3', description: 'Enter deposit amounts' },
  ]

  const [activeStep, setActiveStep] = useState(0)

  const pairDoesNotExists = pairExists === PairState.NOT_EXISTS

  const step0Validate = currencies.CURRENCY_A && currencies.CURRENCY_B
  const step1Validate = !pairDoesNotExists || successCreate

  const handleStep = (newStep: number, force?: boolean) => {
    if (activeStep === 0 && newStep === 1 && step1Validate) {
      // skip create pool signature
      setActiveStep(2)
    } else if (newStep < activeStep || (newStep === 1 && step0Validate) || (newStep === 2 && step1Validate)) {
      setActiveStep(newStep)
    } else if (force === true) {
      setActiveStep(newStep)
    }
  }

  return (
    <>
      <Helmet>
        <title>Add Liquidity to V2 Pools | HyperSwap Exchange on Hyperliquid</title>
        <meta
          name="description"
          content="Add liquidity to V2 pools on HyperSwap Exchange and start earning fees. Built on Hyperliquid's EVM, enjoy fast and efficient transactions."
        />
        <meta
          name="keywords"
          content="HyperSwap V2 liquidity, add liquidity, Hyperliquid blockchain, DeFi liquidity, earn fees, Hyperliquid EVM"
        />
        <meta property="og:title" content="Add Liquidity to V2 Pools | HyperSwap Exchange on Hyperliquid" />
        <meta
          property="og:description"
          content="Join V2 liquidity pools on HyperSwap Exchange and earn trading fees with the speed of Hyperliquid's blockchain."
        />
        <meta property="og:url" content={`${window.location.origin}/#/add/v2/HYPE`} />
      </Helmet>

      <div>
        <AddLiquidityAddRemoveTabs />
        <ScrollablePage>
          <TransactionConfirmationModal
            isOpen={showConfirm}
            onDismiss={handleDismissConfirmation}
            attemptingTxn={attemptingTxn}
            hash={txHash}
            content={() => (
              <ConfirmationModalContent
                title={noLiquidity ? 'You are creating a pool' : 'You will receive'}
                onDismiss={handleDismissConfirmation}
                topContent={modalHeader}
                bottomContent={modalBottom}
              />
            )}
            pendingText={pendingText}
            currencyToAdd={pair?.liquidityToken}
          />
          <AutoColumn>
            <RowContainer>
              <StepperWrapper>
                <CustomAppBody width="100%">
                  <StepperHeader>
                    <h2>New V2 Position</h2>
                  </StepperHeader>
                  <StepperContainer>
                    {steps.map((step, index) => (
                      <Step
                        key={index}
                        clickable={
                          !!(index < activeStep || (index === 1 && step0Validate) || (index === 2 && step1Validate))
                        }
                        onClick={() => handleStep(index)}
                      >
                        <StepNumber active={index === activeStep}>{index + 1}</StepNumber>
                        {index < steps.length - 1 && <StepLine />}
                        <StepContent>
                          <StepTitle active={index === activeStep}>{step.title}</StepTitle>
                          <StepDescription active={index === activeStep}>{step.description}</StepDescription>
                        </StepContent>
                      </Step>
                    ))}
                  </StepperContainer>
                </CustomAppBody>
              </StepperWrapper>

              <MobileStepperWrapper>
                <CustomAppBody width="100%">
                  <StepperContainer>
                    <Step
                      key={activeStep}
                      clickable={
                        !!(
                          activeStep < activeStep ||
                          (activeStep === 1 && step0Validate) ||
                          (activeStep === 2 && step1Validate)
                        )
                      }
                      onClick={() => handleStep(activeStep)}
                    >
                      <StepNumber active={activeStep === activeStep}>{activeStep + 1}</StepNumber>
                      <StepContent>
                        <StepTitle active={activeStep === activeStep}>{steps[activeStep].title}</StepTitle>
                        <StepDescription active={activeStep === activeStep}>
                          {steps[activeStep].description}
                        </StepDescription>
                      </StepContent>
                    </Step>
                  </StepperContainer>
                </CustomAppBody>
              </MobileStepperWrapper>

              <CustomAppBody width={activeStep === 2 ? 'fit-content' : '100%'}>
                <Wrapper>
                  {currencyA && currencyB && (
                    <div style={{ maxWidth: '68px', marginLeft: 'auto' }}>
                      <TagButton onClick={() => clearAll()}>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="icon icon-tabler icons-tabler-outline icon-tabler-restore"
                        >
                          <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                          <path d="M3.06 13a9 9 0 1 0 .49 -4.087" />
                          <path d="M3 4.001v5h5" />
                          <path d="M12 12m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0" />
                        </svg>{' '}
                        Reset
                      </TagButton>
                    </div>
                  )}

                  <AutoColumn gap="32px">
                    {activeStep === 0 && (
                      <>
                        <ColumnCenter>
                          <BlueCard style={{ maxWidth: '500px', marginTop: '12px' }}>
                            <AutoColumn gap="10px">
                              <TYPE.link fontWeight={400} color={'primaryText1'}>
                                <b>Tip:</b> When you add liquidity, you will receive pool tokens representing your
                                position. These tokens automatically earn fees proportional to your share of the pool,
                                and can be redeemed at any time.
                              </TYPE.link>
                            </AutoColumn>
                          </BlueCard>
                        </ColumnCenter>
                        <AutoColumn gap="md">
                          <RowBetween paddingBottom="20px">
                            <TYPE.label>Select pair</TYPE.label>
                          </RowBetween>
                          <RowBetween>
                            <CurrencyDropdown
                              value={formattedAmounts[Field.CURRENCY_A]}
                              onUserInput={onFieldAInput}
                              hideInput={true}
                              onMax={() => {
                                onFieldAInput(maxAmounts[Field.CURRENCY_A]?.toExact() ?? '')
                              }}
                              onCurrencySelect={handleCurrencyASelect}
                              showMaxButton={!atMaxAmounts[Field.CURRENCY_A]}
                              currency={currencies[Field.CURRENCY_A]}
                              id="add-liquidity-input-tokena"
                              showCommonBases
                            />
                            <div style={{ width: '12px' }}></div>

                            <CurrencyDropdown
                              value={formattedAmounts[Field.CURRENCY_B]}
                              hideInput={true}
                              onUserInput={onFieldBInput}
                              onCurrencySelect={handleCurrencyBSelect}
                              onMax={() => {
                                onFieldBInput(maxAmounts[Field.CURRENCY_B]?.toExact() ?? '')
                              }}
                              showMaxButton={!atMaxAmounts[Field.CURRENCY_B]}
                              currency={currencies[Field.CURRENCY_B]}
                              id="add-liquidity-input-tokenb"
                              showCommonBases
                            />
                          </RowBetween>
                        </AutoColumn>
                        <ButtonLight disabled={!step0Validate} onClick={() => handleStep(1)}>
                          Continue
                        </ButtonLight>
                      </>
                    )}

                    {activeStep > 0 && step0Validate && (
                      <StepReview style={{ marginTop: '12px' }}>
                        <span className="title">Step 1</span>
                        <div className="value">
                          <DoubleCurrencyLogo
                            currency0={currencies.CURRENCY_A ?? undefined}
                            currency1={currencies.CURRENCY_B ?? undefined}
                            size={16}
                            margin={true}
                          />
                          <TYPE.label ml="8px" fontSize="14px">
                            {currencies.CURRENCY_A?.symbol} / {currencies.CURRENCY_B?.symbol}
                          </TYPE.label>
                        </div>
                        <button onClick={() => handleStep(0)}>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                            <path d="M7 7h-1a2 2 0 0 0 -2 2v9a2 2 0 0 0 2 2h9a2 2 0 0 0 2 -2v-1" />
                            <path d="M20.385 6.585a2.1 2.1 0 0 0 -2.97 -2.97l-8.415 8.385v3h3l8.385 -8.415z" />
                            <path d="M16 5l3 3" />
                          </svg>
                          Edit
                        </button>
                      </StepReview>
                    )}

                    {activeStep === 1 && (
                      <>
                        {pairDoesNotExists ? (
                          <>
                            <ColumnCenter>
                              <BlueCard style={{ maxWidth: '500px' }}>
                                <AutoColumn gap="10px">
                                  <TYPE.link fontWeight={600} color={'primaryText1'}>
                                    You are the first liquidity provider.
                                  </TYPE.link>
                                  <TYPE.link fontWeight={400} color={'primaryText1'}>
                                    The ratio of tokens you add will set the price of this pool.
                                  </TYPE.link>
                                </AutoColumn>
                              </BlueCard>
                            </ColumnCenter>
                          </>
                        ) : (
                          <></>
                        )}
                        <AutoColumn gap="md" style={{ maxWidth: '500px' }}>
                          <RowBetween paddingBottom="20px">
                            <TYPE.label>Initialize the Pool</TYPE.label>
                          </RowBetween>
                          {pairDoesNotExists ? (
                            <TYPE.link fontWeight={400} color={'text2'}>
                              This pool does not exist yet on the protocol. To initialize it, you need to sign a message
                              with your wallet. This signature is completely gasless (no transaction fees) and allows
                              the pair to be created on the network.
                            </TYPE.link>
                          ) : (
                            <TYPE.link fontWeight={400} color={'text2'}>
                              This pool has already been initialized on the protocol. This step is not required, and you
                              can proceed to the next step.
                            </TYPE.link>
                          )}
                        </AutoColumn>
                        <AutoColumn gap="md" style={{ maxWidth: '500px' }}>
                          <ButtonPrimary
                            disabled={!pairDoesNotExists || loadingCreate || successCreate}
                            onClick={() => onCreate()}
                          >
                            {loadingCreate ? <Dots>Loading</Dots> : 'Sign to initialize the pool'}
                          </ButtonPrimary>
                          <ButtonLight disabled={!step1Validate} onClick={() => handleStep(2)}>
                            Continue
                          </ButtonLight>

                          {errorCreate.length > 0 && !step1Validate && (
                            <TYPE.link fontWeight={400} color={'error'}>
                              {errorCreate}
                            </TYPE.link>
                          )}
                        </AutoColumn>
                      </>
                    )}

                    {activeStep > 1 && step1Validate && (
                      <StepReview>
                        <span className="title">Step 2</span>
                        <div className="value">Pool Has Been Initialized</div>
                        <button onClick={() => handleStep(1)}>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                            <path d="M7 7h-1a2 2 0 0 0 -2 2v9a2 2 0 0 0 2 2h9a2 2 0 0 0 2 -2v-1" />
                            <path d="M20.385 6.585a2.1 2.1 0 0 0 -2.97 -2.97l-8.415 8.385v3h3l8.385 -8.415z" />
                            <path d="M16 5l3 3" />
                          </svg>
                          Edit
                        </button>
                      </StepReview>
                    )}

                    {activeStep === 2 && (
                      <>
                        <AutoColumn gap="md">
                          <RowBetween paddingBottom="20px">
                            <TYPE.label>Deposit amounts</TYPE.label>
                          </RowBetween>
                        </AutoColumn>
                        <AutoColumn gap="20px">
                          <TYPE.label fontWeight={400}>
                            Enter the amounts of tokens you want to add to the liquidity pool
                          </TYPE.label>

                          <CurrencyInputPanel
                            value={formattedAmounts[Field.CURRENCY_A]}
                            onUserInput={onFieldAInput}
                            onMax={() => {
                              onFieldAInput(maxAmounts[Field.CURRENCY_A]?.toExact() ?? '')
                            }}
                            showMaxButton={!atMaxAmounts[Field.CURRENCY_A]}
                            currency={currencies[Field.CURRENCY_A]}
                            id="add-liquidityv2-input-tokena"
                            showCommonBases
                          />
                          <ColumnCenter>
                            <Plus size="16" color={theme.text2} />
                          </ColumnCenter>
                          <CurrencyInputPanel
                            value={formattedAmounts[Field.CURRENCY_B]}
                            onUserInput={onFieldBInput}
                            onMax={() => {
                              onFieldBInput(maxAmounts[Field.CURRENCY_B]?.toExact() ?? '')
                            }}
                            showMaxButton={!atMaxAmounts[Field.CURRENCY_B]}
                            currency={currencies[Field.CURRENCY_B]}
                            id="add-liquidityv2-input-tokenb"
                            showCommonBases
                          />
                          {currencies[Field.CURRENCY_A] &&
                            currencies[Field.CURRENCY_B] &&
                            pairState !== PairState.INVALID && (
                              <>
                                <LightCard padding="0px" borderRadius={'20px'}>
                                  <RowBetween padding="1rem">
                                    <TYPE.subHeader fontWeight={500} fontSize={14}>
                                      {noLiquidity ? 'Initial prices' : 'Prices'} and pool share
                                    </TYPE.subHeader>
                                  </RowBetween>{' '}
                                  <LightCard padding="1rem" borderRadius={'20px'}>
                                    <PoolPriceBar
                                      currencies={currencies}
                                      poolTokenPercentage={poolTokenPercentage}
                                      noLiquidity={noLiquidity}
                                      price={price}
                                    />
                                  </LightCard>
                                </LightCard>
                              </>
                            )}

                          {farmingPoolExists && (
                            <>
                              <ColumnCenter>
                                <BlueCard style={{ maxWidth: '500px' }}>
                                  <AutoColumn gap="10px">
                                    <TYPE.link fontWeight={400} color={'primaryText1'}>
                                      <b>Detected:</b> A new farming pool has been detected. You can add liquidity and
                                      automatically stake your LP tokens in the liquidity mining campaign.
                                    </TYPE.link>
                                  </AutoColumn>
                                </BlueCard>
                              </ColumnCenter>
                              <ColumnCenter>
                                <Tabs>
                                  <StyledTabButton
                                    isActive={!addInFarmingPool}
                                    onClick={() => setAddInFarmingPool(false)}
                                  >
                                    Add Liquidity Only
                                  </StyledTabButton>
                                  <StyledTabButton
                                    isActive={addInFarmingPool}
                                    onClick={() => setAddInFarmingPool(true)}
                                  >
                                    Add Liquidity and Stake
                                  </StyledTabButton>
                                </Tabs>
                              </ColumnCenter>
                            </>
                          )}

                          {addIsUnsupported ? (
                            <ButtonPrimary disabled={true}>
                              <TYPE.main mb="4px">Unsupported Asset</TYPE.main>
                            </ButtonPrimary>
                          ) : !account ? (
                            <ButtonLight onClick={toggleWalletModal}>Connect Wallet</ButtonLight>
                          ) : (
                            <AutoColumn gap={'md'}>
                              {farmingPoolExists && addInFarmingPool ? (
                                <>
                                  {(approvalAForCampaign === ApprovalState.NOT_APPROVED ||
                                    approvalAForCampaign === ApprovalState.PENDING ||
                                    approvalBForCampaign === ApprovalState.NOT_APPROVED ||
                                    approvalBForCampaign === ApprovalState.PENDING) &&
                                    isValid && (
                                      <RowBetween>
                                        {approvalAForCampaign !== ApprovalState.APPROVED && (
                                          <ButtonPrimary
                                            onClick={approveAForCampaignCallback}
                                            disabled={approvalAForCampaign === ApprovalState.PENDING}
                                            width={approvalBForCampaign !== ApprovalState.APPROVED ? '48%' : '100%'}
                                          >
                                            {approvalAForCampaign === ApprovalState.PENDING ? (
                                              <Dots>Approving {currencies[Field.CURRENCY_A]?.symbol}</Dots>
                                            ) : (
                                              'Approve ' + currencies[Field.CURRENCY_A]?.symbol
                                            )}
                                          </ButtonPrimary>
                                        )}
                                        {approvalBForCampaign !== ApprovalState.APPROVED && (
                                          <ButtonPrimary
                                            onClick={approveBForCampaignCallback}
                                            disabled={approvalBForCampaign === ApprovalState.PENDING}
                                            width={approvalAForCampaign !== ApprovalState.APPROVED ? '48%' : '100%'}
                                          >
                                            {approvalBForCampaign === ApprovalState.PENDING ? (
                                              <Dots>Approving {currencies[Field.CURRENCY_B]?.symbol}</Dots>
                                            ) : (
                                              'Approve ' + currencies[Field.CURRENCY_B]?.symbol
                                            )}
                                          </ButtonPrimary>
                                        )}
                                      </RowBetween>
                                    )}
                                </>
                              ) : (
                                <>
                                  {(approvalA === ApprovalState.NOT_APPROVED ||
                                    approvalA === ApprovalState.PENDING ||
                                    approvalB === ApprovalState.NOT_APPROVED ||
                                    approvalB === ApprovalState.PENDING) &&
                                    isValid && (
                                      <RowBetween>
                                        {approvalA !== ApprovalState.APPROVED && (
                                          <ButtonPrimary
                                            onClick={approveACallback}
                                            disabled={approvalA === ApprovalState.PENDING}
                                            width={approvalB !== ApprovalState.APPROVED ? '48%' : '100%'}
                                          >
                                            {approvalA === ApprovalState.PENDING ? (
                                              <Dots>Approving {currencies[Field.CURRENCY_A]?.symbol}</Dots>
                                            ) : (
                                              'Approve ' + currencies[Field.CURRENCY_A]?.symbol
                                            )}
                                          </ButtonPrimary>
                                        )}
                                        {approvalB !== ApprovalState.APPROVED && (
                                          <ButtonPrimary
                                            onClick={approveBCallback}
                                            disabled={approvalB === ApprovalState.PENDING}
                                            width={approvalA !== ApprovalState.APPROVED ? '48%' : '100%'}
                                          >
                                            {approvalB === ApprovalState.PENDING ? (
                                              <Dots>Approving {currencies[Field.CURRENCY_B]?.symbol}</Dots>
                                            ) : (
                                              'Approve ' + currencies[Field.CURRENCY_B]?.symbol
                                            )}
                                          </ButtonPrimary>
                                        )}
                                      </RowBetween>
                                    )}
                                </>
                              )}

                              <ButtonError
                                onClick={() => {
                                  expertMode ? onAdd() : setShowConfirm(true)
                                }}
                                disabled={!isValid || swapIsInvalidWithApproval}
                                error={
                                  !isValid && !!parsedAmounts[Field.CURRENCY_A] && !!parsedAmounts[Field.CURRENCY_B]
                                }
                              >
                                <Text fontSize={20} fontWeight={500}>
                                  {error ?? 'Supply'}
                                </Text>
                              </ButtonError>
                            </AutoColumn>
                          )}

                          {!addIsUnsupported ? (
                            pair && !noLiquidity && pairState !== PairState.INVALID ? (
                              <AutoColumn
                                style={{ minWidth: '20rem', width: '100%', marginTop: '1rem', maxWidth: '500px' }}
                              >
                                <MinimalPositionCard showUnwrapped={oneCurrencyIsWETH} pair={pair} />
                              </AutoColumn>
                            ) : null
                          ) : (
                            <UnsupportedCurrencyFooter
                              show={addIsUnsupported}
                              currencies={[currencies.CURRENCY_A, currencies.CURRENCY_B]}
                            />
                          )}
                        </AutoColumn>
                      </>
                    )}
                  </AutoColumn>
                </Wrapper>
              </CustomAppBody>
            </RowContainer>
          </AutoColumn>
        </ScrollablePage>
      </div>
    </>
  )
}
